/* eslint-disable no-unused-vars */
import React, { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import moment from 'moment';

import ParticipationService from '../../../../../../services/ParticipationService';
import LoanService from '../../../../../../services/LoanService';
import useUserRole from '../../../../../../hooks/useUserRole';
import LenderService from '../../../../../../services/LenderService';

export default function Explanation({
  lenderHistoryElement,
  loanId,
  lenderHistory,
  index,
}) {
  const { isLender } = useUserRole();

  const isLast = lenderHistory?.length === index + 1;

  const { data: participations } = useQuery({
    queryKey: ['participations', loanId],
    queryFn: isLender
      ? () => LoanService.getLoanParticipation(loanId)
      : () => ParticipationService.getParticipations({ loanId }),
    enabled: !!loanId,
  });

  const { data: lender } = useQuery({
    queryKey: ['lender', lenderHistoryElement?.lender_id],
    queryFn: () => LenderService.getLender(lenderHistoryElement?.lender_id),
    enabled: !!lenderHistoryElement?.lender_id,
  });

  const nearbyParticipations = useMemo(
    () =>
      participations?.filter((participation) => {
        if (isLast) {
          return moment(participation.start_date).isSameOrAfter(
            lenderHistoryElement?.effective_date
          );
        }

        return (
          moment(participation.start_date).isSameOrAfter(
            lenderHistoryElement?.effective_date
          ) &&
          moment(participation.start_date).isBefore(
            lenderHistory[index + 1]?.effective_date
          )
        );
      }),
    [
      index,
      isLast,
      lenderHistory,
      lenderHistoryElement?.effective_date,
      participations,
    ]
  );

  const formattedEffectiveDate = lenderHistoryElement.effective_date
    ? moment(lenderHistoryElement.effective_date).format('M/D/YY')
    : null;
  const formattedInterestEffectiveDate =
    lenderHistoryElement.interest_effective_date
      ? moment(lenderHistoryElement.interest_effective_date).format('M/D/YY')
      : null;

  if (
    !lenderHistoryElement ||
    !lenderHistoryElement.effective_date ||
    !lenderHistoryElement.interest_effective_date ||
    !lenderHistoryElement.lender_id
  ) {
    return null;
  }

  if (!nearbyParticipations?.length) {
    return (
      <div className="text-sm text-slate-600 dark:text-slate-400">{`${lender?.name} acquired the loan on ${formattedEffectiveDate}. Interest covering ${formattedInterestEffectiveDate} moving forward will go to ${lender?.name}.`}</div>
    );
  }

  return (
    <div className="text-sm text-slate-600 dark:text-slate-400">
      {`${lender?.name} acquired the loan on ${formattedEffectiveDate}. Interest covering ${formattedInterestEffectiveDate} moving forward will go to ${lender?.name}. Interest will be prorated and distributed to participants as follows:`}
      <ul className="list-disc list-inside">
        {nearbyParticipations?.map((participation) => (
          <li key={participation.id}>
            {`Starting on ${moment(participation.start_date).format(
              'M/D/YY'
            )}, ${
              participation?._lender?.name ||
              (participation?._servicer?.name ?? 'servicer')
            }`}{' '}
            will participate in the at{' '}
            {((participation?.rate ?? 0) * 100).toFixed(3)}% rate.
          </li>
        ))}
      </ul>
    </div>
  );
}
